import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { format } from 'date-fns';
import { ISlidePropostaData } from '../interface';

const { Title, Text } = Typography;

export interface SlideContainerProps {
  children: React.ReactNode;
  showFooterLogo?: boolean;
  showWaves?: boolean;
  noPadding?: boolean;
  backgroundImage?: string;
  scale?: number;
  data?: ISlidePropostaData;
  showWatermark?: boolean;
  style?: React.CSSProperties;
}

export const SlideContainer: React.FC<SlideContainerProps> = ({
  children,
  showFooterLogo = true,
  showWaves = true,
  noPadding = false,
  backgroundImage,
  scale = 1,
  data,
  showWatermark = true,
  style,
}) => {
  const watermarkText = useMemo(() => {
    if (showWatermark === false) return null;

    let idStr = '';
    if (data?.id !== undefined && data?.id !== null) {
      idStr = `Proposta Nº ${data.id}`;
    }

    const dataRef = data?.gerado_em ? new Date(data.gerado_em) : new Date();
    const dataStr = !isNaN(dataRef.getTime())
      ? format(dataRef, 'dd/MM/yyyy HH:mm')
      : format(new Date(), 'dd/MM/yyyy HH:mm');

    if (!idStr) {
      return `Proposta Nº X`/*• ${dataStr}*/;
    }

    return `${idStr}` /*• ${dataStr}*/;
  }, [data?.id, data?.gerado_em, showWatermark]);

  const content = (
    <div
      className="slide-container-root"
      style={{
        width: 1920,
        height: 1080,
        backgroundColor: '#ffffff',
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'hidden',
        padding: noPadding ? 0 : 64,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxShadow: scale === 1 ? '0 20px 25px -5px rgba(0, 0, 0, 0.1)' : 'none',
        margin: scale === 1 ? '0 auto' : '0 auto',
        fontFamily: 'sans-serif',
        breakAfter: 'page',
        transform: scale !== 1 ? `scale(${scale})` : undefined,
        transformOrigin: 'top left',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...style,
      }}
    >
      {/* Conteúdo principal na camada da frente */}
      <div style={{ position: 'relative', zIndex: 10, width: '100%', height: '100%' }}>
        {children}
      </div>

      {/* Marca d'água de identificação no canto inferior esquerdo */}
      {watermarkText && (
        <div
          style={{
            position: 'absolute',
            bottom: 22,
            left: 28,
            zIndex: 80,
            pointerEvents: 'none',
            fontSize: 17,
            fontWeight: 700,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            color: 'rgba(255, 255, 255, 0.90)',
            backgroundColor: 'rgba(15, 23, 42, 0.35)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            padding: '7px 18px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.20)',
            letterSpacing: '0.4px',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {watermarkText}
        </div>
      )}

      {/* Ondas azuis decorativas (se ativadas) */}
      {showWaves && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 160,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <svg viewBox="0 0 1920 160" style={{ width: '100%', height: '100%' }} fill="none">
            <path
              d="M0 160C450 160 700 80 1200 110C1600 134 1750 60 1920 70V160H0Z"
              fill="#9ca3af"
              fillOpacity="0.3"
            />
            <path
              d="M0 160C380 160 620 40 1100 85C1520 120 1700 20 1920 30V160H0Z"
              fill="#0284c7"
            />
          </svg>
        </div>
      )}

      {/* Logo do Rodapé */}
      {showFooterLogo && (
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            textAlign: 'center',
          }}
        >
          <Title level={3} style={{ margin: 0, color: '#0284c7', fontWeight: 900, letterSpacing: -0.5 }}>
            GOV<span style={{ color: '#65a30d' }}>FÁCIL</span>
          </Title>
          <Text
            type="secondary"
            style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 600 }}
          >
            O jeito fácil de governar
          </Text>
        </div>
      )}
    </div>
  );

  if (scale !== 1) {
    return (
      <div
        style={{
          width: 1920 * scale,
          height: 1080 * scale,
          overflow: 'hidden',
          position: 'relative',
          margin: '0 auto',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          borderRadius: '4px',
        }}
      >
        {content}
      </div>
    );
  }

  return content;
};